import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from 'utils/number';
import styled from 'styled-components';

interface VoucherProps {
  voucherCode: string;
  applyVoucher: (code: string) => void;
  clearVoucher: () => void;
  payWithVoucher: () => void;
  error: string;
  loading: boolean;
  voucherDiscount: number;
  coversFullAmount: boolean;
}

const VoucherLayout = styled.section`
  width: 100%;
  margin-top: 14px;
`;

const VoucherHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const VoucherTitle = styled.h3`
  margin: 0;
  color: #1a253d;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border: 1px solid #dbe4ee;
  border-radius: 11px;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: #259dcc;
    box-shadow: 0 0 0 3px rgba(37, 157, 204, 0.12);
  }
`;

const VoucherInput = styled.input`
  min-width: 0;
  flex: 1;
  height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #17223b;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  outline: none;
  text-transform: uppercase;

  &::placeholder {
    color: #9aabc0;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ApplyButton = styled.button`
  height: 40px;
  min-width: 100px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #17223b;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, transform 0.2s;

  &:hover:not(:disabled) {
    background: #243454;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    background: #eef1f5;
    color: #8b95a5;
  }
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  min-height: 38px;
  margin-top: 10px;
  padding: 0 12px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fff5f5;
  color: #c53030;
  font-size: 12px;
  line-height: 1.35;
`;

const AppliedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  border: 1px solid #b8dfed;
  border-radius: 12px;
  background: linear-gradient(135deg, #f2fbff, #edf7fb);
`;

const AppliedInfo = styled.div`
  min-width: 0;
`;

const AppliedLabel = styled.div`
  margin-bottom: 4px;
  color: #317293;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const AppliedCode = styled.div`
  overflow: hidden;
  color: #17223b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AppliedActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const AppliedDiscount = styled.div`
  color: #087aa8;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: #5b7088;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover:not(:disabled) {
    color: #c53030;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const PayVoucherButton = styled.button`
  width: 100%;
  min-height: 52px;
  margin-top: 12px;
  padding: 0 16px;
  border: 0;
  border-radius: 11px;
  background: #17223b;
  box-shadow: 0 7px 16px rgba(23, 34, 59, 0.18);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover:not(:disabled) {
    background: #243454;
    box-shadow: 0 9px 20px rgba(23, 34, 59, 0.24);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    box-shadow: none;
  }
`;

const Voucher = ({
  voucherCode,
  applyVoucher,
  clearVoucher,
  payWithVoucher,
  error,
  loading,
  voucherDiscount,
  coversFullAmount,
}: VoucherProps) => {
  const { t } = useTranslation();
  const [inputCode, setInputCode] = useState('');
  const title = t('voucher') === 'voucher' ? 'Gutschein' : t('voucher');
  const removeLabel = t('removeVoucher') === 'removeVoucher' ? 'Entfernen' : t('removeVoucher');
  const payLabel = t('payWithVoucher') === 'payWithVoucher' ? 'Mit Gutschein bezahlen' : t('payWithVoucher');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputCode.trim()) {
      applyVoucher(inputCode.trim());
    }
  };

  return (
    <VoucherLayout>
      <VoucherHeader>
        <VoucherTitle>{title}</VoucherTitle>
      </VoucherHeader>

      {!voucherCode ? (
        <form onSubmit={handleSubmit}>
          <InputWrapper>
            <VoucherInput
              type="text"
              placeholder={t('voucherCodePlaceholder') === 'voucherCodePlaceholder' ? 'Gutschein-Code' : t('voucherCodePlaceholder')}
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              disabled={loading}
              autoComplete="off"
              aria-label={title}
            />
            <ApplyButton type="submit" disabled={loading || !inputCode.trim()}>
              {loading ? 'Wird geprüft …' : (t('apply') === 'apply' ? 'Anwenden' : t('apply'))}
            </ApplyButton>
          </InputWrapper>
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </form>
      ) : (
        <>
          <AppliedCard>
            <AppliedInfo>
              <AppliedLabel>Gutschein angewendet</AppliedLabel>
              <AppliedCode title={voucherCode}>{voucherCode}</AppliedCode>
            </AppliedInfo>
            <AppliedActions>
              <AppliedDiscount>-{currencyFormat(voucherDiscount)}</AppliedDiscount>
              <RemoveButton type="button" onClick={clearVoucher} disabled={loading}>
                {removeLabel}
              </RemoveButton>
            </AppliedActions>
          </AppliedCard>
          {coversFullAmount && (
            <PayVoucherButton type="button" onClick={payWithVoucher} disabled={loading}>
              {loading ? 'Zahlung wird verarbeitet …' : payLabel}
            </PayVoucherButton>
          )}
        </>
      )}
    </VoucherLayout>
  );
};

export default Voucher;
